'use strict';

/*

Coordinates the users journey down a pre-specified set of sequential steps (pages)

To determine the next page a user should see we cycle through a set of steps,
checking the `completed` condition of each step.
If a completed condition is NOT met,
it means take the user to that step.

*/

function _OnboardingCoordinator() {
  this.id = null;
  this.node = null;
  this.declaredClass = null;
  this.extends = [_Paths];

  this.flows = [];
  this.activeFlow = '';
  this.activeSet = null; // Phase 3
  this._DEBUG = false;

  this.init = function () {};

  this.initAllReady = function () {};

  this.initOnboardingCoordinator = function () {
    this.availableSteps = {
      verify: {
        route: this.paths.signup.verify,
        completed: 'hasVerifiedEmail'
      },
      create_profile_step1: {
        route: this.paths.signup.create_profile,
        prerequisites: 'hasVerifiedEmail',
        completed: 'hasAllBasicInfo'
      },
      create_profile_step2: {
        route: this.paths.signup.create_profile_step2,
        prerequisites: 'hasAllBasicInfo',
        completed: 'hasAllProfileInfo'
      },
      product_interests: {
        route: this.paths.signup.product_interests,
        completed: 'hasSelectedProductInterests'
      },
      credit_request_reasons: {
        route: this.paths.signup.credit_request_reasons,
        completed: 'hasCreditRequestReasons'
      },
      legal_name: {
        route: this.paths.signup.legal_name,
        completed: 'hasLegalName'
      },
      dob: {
        route: this.paths.signup.dob,
        completed: 'hasDateOfBirth'
      },
      address: {
        route: this.paths.signup.address,
        completed: 'hasAddress',
        prerequisites: 'hasAllBasicInfo'
      },
      phone: {
        route: this.paths.signup.phone,
        completed: 'hasPhoneNumber'
      },
      residential_status: {
        route: this.paths.signup.residential_status,
        completed: 'hasResidentialStatus'
      },
      residential_details: {
        route: this.paths.signup.verresidential_detailsify,
        completed: 'hasResidentialDetails'
      },
      employment_information: {
        route: this.paths.signup.employment_information,
        prerequisites: 'hasVerifiedEmail', // phase 2
        completed: 'hasEmploymentInformation'
      },
      provide_consent: {
        route: this.paths.signup.provide_consent,
        completed: 'hasProvidedConsent'
      },
      underage: {
        route: this.paths.signup.declined.underage,
        completed: 'hasValidAge'
      },
      province: {
        route: this.paths.signup.declined.province,
        completed: 'hasValidProvince'
      },
      province_underage: {
        route: this.paths.signup.declined.province_underage,
        completed: 'hasValidProvinceAge'
      },
      kyc_questions: {
        route: this.paths.signup.kyc_questions,
        completed: 'hasPassedKYC'
      },
      credit_check: {
        route: this.paths.signup.credit_check,
        completed: 'hasDoneCreditCheck'
      },
      verification_level: {
        route: this.paths.signup.declined.kyc_denied,
        completed: 'hasValidVerificationLevel'
      },
      lender_licence: {
        route: this.paths.signup.lender_licence,
        completed: 'hasSeenLenderLicense'
      },
      dashboard: {
        route: this.paths.account.dashboard
      },
      preapproval: {
        route: this.paths.loans.preapproval
      }
    };

    // single long form page using old create_profile display and FE validation
    this.flows.loans_longform = ['verify', 'create_profile_step1', // doesn't contain address anymore - https://mogofintech.atlassian.net/browse/ACCT-1016
    'create_profile_step2', // optional
    'provide_consent', // optional (consent)
    'underage', // optional
    'province', // optional
    'province_underage', // optional
    'kyc_questions', 'credit_check', 'verification_level', 'lender_licence', // optional
    'dashboard'];

    // single long form page using old create_profile display and FE validation
    // This flow splits up address onto its own screen
    // https://mogofintech.atlassian.net/browse/ACCT-1016
    this.flows.split_up_profile_info = ['verify', 'create_profile_step1', // doesn't contain address anymore - https://mogofintech.atlassian.net/browse/ACCT-1016
    'address', // address is on own page here - https://mogofintech.atlassian.net/browse/ACCT-1016
    'create_profile_step2', // optional
    'provide_consent', // optional (consent)
    'underage', // optional
    'province', // optional
    'province_underage', // optional
    'kyc_questions', 'credit_check', 'verification_level', 'lender_licence', // optional
    'dashboard'];

    // with everything (Loans)
    this.flows.long = ['verify', 'product_interests', 'legal_name', 'dob', 'address', 'phone', 'underage', // optional
    'province', // optional
    'province_underage', // optional
    'kyc_questions', 'credit_request_reasons', 'residential_status', 'residential_details', 'employment_information', 'provide_consent', // should be after kyc...
    'credit_check', // phase 1 hardcoded to 1 step. (requires emp info & consent)

    // 'lender_licence', // optional (going away)
    // 'freebie', // future upsale functionality
    'preapproval'];

    // with credit check (Credit Score, Mortgage)
    this.flows.standard = ['verify', 'product_interests', 'legal_name', 'dob', 'address', 'phone', 'underage', // optional
    'province', // optional
    'province_underage', // optional
    'kyc_questions', 'provide_consent', 'credit_check', // (requires emp so wont work as it is now)

    'dashboard'];

    // without credit check ( Card)
    this.flows.basic = ['verify', 'product_interests', 'legal_name', 'dob', 'address', 'phone', 'underage', // optional
    'province', // optional
    'province_underage', // optional
    'kyc_questions', 'dashboard'];

    // Crypto (phase 6)
    this.flows.crypto = ['verify', 'product_interests', 'watch'];
    this.flows.cryptofund = ['legal_name', 'dob', 'address', 'provide_consent', 'kyc_questions', 'credit_check', 'fund'];
  };

  this.setOnboardingFlow = function () {
    // Phase 1: lock it down:
    // const _flowStub = 'loans_longform';

    // This flow splits up address onto its own screen
    // https://mogofintech.atlassian.net/browse/ACCT-1016
    var _flowStub = 'split_up_profile_info';

    // var _interestsArr = this.presenter.getModelProperty('account.products_of_interest');
    // if (flowName) {
    //   _flowStub = flowName; // force mode (phase1)
    // } else if (!_interestsArr) {
    //   console.warn('products_of_interest not set');
    //   _flowStub = 'loans_longform';
    // } else if (_interestsArr.includes('loan')) {
    //   _flowStub = 'long';
    // } else if (
    //   _interestsArr.includes('monthly_credit_score') ||
    //   _interestsArr.includes('mortgage')
    // ) {
    //   _flowStub = 'standard';
    // } else {
    //   _flowStub = 'basic';
    // }

    if (this._DEBUG) console.log('Setting Onboarding Flow to [%s]', _flowStub);

    this.activeFlow = _flowStub;
  };

  this.getCurrentOnboardingFlow = function () {
    // TODO:
  };

  // find the first step in the ordered array that the user has not passed
  this._getFirstIncompleteStep = function (flowName) {
    if (this._DEBUG) {
      console.log('flow name is >>' + flowName);
      console.info('Finding Onboarding Flow Next Step .................');
    }
    var steps = this.flows[flowName];

    for (var i = 0; i < steps.length; i++) {
      var _stepObj = this.availableSteps[steps[i]];
      var requiredCheck = this[_stepObj.completed];

      if (requiredCheck === undefined) {
        if (this._DEBUG) console.log('No check found for', _stepObj.route);
      } else {
        requiredCheck = requiredCheck.bind(this);
      }
      var passedAllChecks = requiredCheck === undefined || requiredCheck.call(); // must pass check if one exists.

      if (!passedAllChecks) {
        if (this._DEBUG) {
          console.info('%c\u2717 FAILED [%s] for step [%s]', 'color: #ff6700;', _stepObj.completed, _stepObj.route);
        }
        return _stepObj.route;
      }
      if (this._DEBUG) {
        console.info('%c\u2713 PASSED [%s] for step [%s]', 'color: #06A50C;', _stepObj.completed, _stepObj.route);
      }

      // Final step reached:
      if (i === steps.length - 1) {
        if (this._DEBUG) console.info('%c\u2714 COMPLETED Onboarding flow', 'color: #0bdc60;');
        return _stepObj.route;
      }
    }
  };

  this.routeToNextStep = function () {
    if (!this.activeFlow) {
      if (this._DEBUG) console.log('routeToNextStep: !this.activeFlow : Setting default');
      this.setOnboardingFlow();
    }
    var _nextStep = this._getFirstIncompleteStep(this.activeFlow);
    this.history.goToUrl(_nextStep);
  };

  this.routeToPreviousStep = function () {
    // TODO: phase 3
  };

  // ################# CHECKS #################

  this.hasVerifiedEmail = function () {
    return this.presenter.models.account.confirmed_at && !this.presenter.models.account.unconfirmed_email;
  };

  this.hasSelectedProductInterests = function () {
    return this.presenter.getModelProperty('account.products_of_interest');
  };

  this.hasCreditRequestReasons = function () {
    var _interestsArr = this.presenter.getModelProperty('account.products_of_interest');
    return !_interestsArr.includes('loan') || this.presenter.getModelProperty('account.requested_credit_amount') && this.presenter.getModelProperty('account.credit_purpose');
  };

  this.hasLegalName = function () {
    return !!this.presenter.getModelProperty('account.first_name') && !!this.presenter.getModelProperty('account.last_name');
  };

  this.hasDateOfBirth = function () {
    return !!this.presenter.getModelProperty('account.birth_date');
  };

  this.hasAddress = function () {
    return !!this.presenter.getModelProperty('account.address') && !!this.presenter.getModelProperty('account.city') && !!this.presenter.getModelProperty('account.province') && !!this.presenter.getModelProperty('account.postal_code');
  };

  this.hasPhoneNumber = function () {
    return !!this.presenter.getModelProperty('account.phone_number');
  };

  this.hasResidentialStatus = function () {
    return !!this.presenter.getModelProperty('account.residential_status');
  };

  this.hasResidentialDetails = function () {
    return this.presenter.getModelProperty('account.residential_monthly_cost') !== null;
  };

  this.hasHousingDetails = function () {
    return this.hasResidentialStatus() && this.hasResidentialDetails();
  };

  this.hasEmploymentInformation = function () {
    // return !!this.presenter.getModelProperty('account.employment_type');
    return !!this.presenter.getModelProperty('account.employment_type') && (this.presenter.getModelProperty('account.employment_type') !== 'Employed' || this.presenter.getModelProperty('account.employment_type') === 'Employed' && !!this.presenter.getModelProperty('account.months_employed'));
  };

  this.hasAllBasicInfo = function () {
    return this.hasLegalName() && this.hasDateOfBirth() && (this.hasSelectedProductInterests() ||
    // legacy user without POI can skip the step:
    !this.hasSelectedProductInterests() && this.hasPassedKYC());
  };

  this.hasAllProfileInfo = function () {
    return (
      // ensure the checks being done are based on _saved_ data rather than data in memory (
      // ex: if a user selects employment details in form but clicks browser back button instead of submitting form
      !this.presenter.getModelProperty('account.onboarding_step2_isModified') &&
      // signup_ip_address: some old/edge case users were missing this field
      !!this.presenter.getModelProperty('account.signup_ip_address') && this.hasAllBasicInfo() && this.hasAddress() && this.hasCompletedOrCanSkipAdditionalInfo()
    );
  };

  this.hasCompletedOrCanSkipAdditionalInfo = function () {
    var _interestsArr = this.presenter.getModelProperty('account.products_of_interest');
    return _interestsArr === null || !!_interestsArr && !_interestsArr.includes('loan') || !!_interestsArr && _interestsArr.includes('loan') && this.hasEmploymentInformation() && this.hasResidentialDetails();
  };

  this.hasValidAge = function () {
    return this.presenter.getModelProperty('account.valid_age');
  };

  this.hasValidProvince = function () {
    return this.presenter.getModelProperty('account.valid_province');
  };

  this.hasValidProvinceAge = function () {
    return this.hasValidProvince() && this.hasValidAge();
  };

  this.hasProvidedConsent = function () {
    return !!this.presenter.getModelProperty('account.credit_check_opt_in');
  };

  this.hasPassedKYC = function () {
    return !!this.presenter.getModelProperty('account.kyc_decision') && this.presenter.getModelProperty('account.kyc_decision') !== 'timeout' && this.presenter.getModelProperty('account.kyc_decision') !== 'error' && this.presenter.getModelProperty('account.kyc_decision') !== 'retry' && (this.presenter.hasAction('account.get_credit_detail') || this.hasDoneCreditCheck());
  };

  this.hasDoneCreditCheck = function () {
    return !!this.presenter.getModelProperty('account.credit_check_completed_at') && !!this.presenter.getModelProperty('account.credit_check_decision') && this.presenter.getModelProperty('account.credit_check_decision') !== 'timeout' && this.presenter.getModelProperty('account.credit_check_decision') !== 'error' && this.presenter.getModelProperty('account.credit_check_decision') !== 'retry';
  };

  this.hasValidVerificationLevel = function () {
    // the user has done a credit check but equifax flat out denied them
    return this.presenter.getModelProperty('account.verification_level') !== 4;
  };

  // Optional Step for loans...
  this.hasSeenLenderLicense = function () {
    var hasZip = false; // TODO: DONT NEED THIS PROBABLY BECAUSE ZIP GOING BYE BYE
    var requiredStep = hasZip && (this.presenter.models.account.province === 'BC' || this.presenter.models.account.province === 'AB' || this.presenter.models.account.province === 'ON');

    return !requiredStep;
  };

  this.destroy = function () {};
}